import React, { useState, useEffect } from "react";
import styles from "./TimeTable.module.scss";

interface TimeTableProps {
  selectedDay: string;
  onTimeChange: (times: string[]) => void;
  initialTimes?: string[];
}

export const TimeTable: React.FC<TimeTableProps> = ({ selectedDay, onTimeChange, initialTimes }) => {
  const [selectedTimes, setSelectedTimes] = useState<string[]>(initialTimes || []);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Инициализация с initialTimes при первой загрузке
  useEffect(() => {
    if (!hasInitialized && initialTimes && initialTimes.length > 0) {
      setSelectedTimes(initialTimes);
      onTimeChange(initialTimes);
      setHasInitialized(true);
    }
  }, [initialTimes, hasInitialized, onTimeChange]);

  // Сброс при изменении дня
  useEffect(() => {
    if (hasInitialized) {
      setSelectedTimes([]);
      onTimeChange([]);
    }
  }, [selectedDay, hasInitialized, onTimeChange]);

  const generateTimeSlots = () => {
    const slots: string[] = [];
    for (let hour = 7; hour <= 23; hour++) {
      slots.push(`${hour.toString().padStart(2, "0")}:00`);
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const isTimeDisabled = (time: string): boolean => {
    if (!selectedDay) return false;

    const now = new Date();
    const selectedDate = new Date(selectedDay);

    const isToday = selectedDate.toDateString() === now.toDateString();

    if (isToday) {
      const currentHour = now.getHours();
      const timeHour = parseInt(time.split(":")[0]);
      return timeHour <= currentHour;
    }

    return selectedDate < new Date(now.setHours(0, 0, 0, 0));
  };

  const handleTimePress = (time: string) => {
    if (isTimeDisabled(time)) {
      return;
    }

    let newSelectedTimes = [...selectedTimes];
    const timeHour = parseInt(time.split(":")[0]);

    if (selectedTimes.includes(time)) {
      // Удаляем время из выбранных
      newSelectedTimes = newSelectedTimes.filter((t) => t !== time);
    } else {
      if (selectedTimes.length === 0) {
        // Если ничего не выбрано, просто добавляем
        newSelectedTimes = [time];
      } else {
        // Проверяем, можем ли мы добавить это время к существующему интервалу
        const hours = selectedTimes.map((t) => parseInt(t.split(":")[0])).sort();
        const minHour = hours[0];
        const maxHour = hours[hours.length - 1];

        // Можно добавить только если время соседнее к началу или концу интервала
        if (timeHour === minHour - 1 || timeHour === maxHour + 1) {
          newSelectedTimes.push(time);
          newSelectedTimes.sort();
        } else {
          // Если не соседнее, начинаем новый выбор
          newSelectedTimes = [time];
        }
      }
    }

    setSelectedTimes(newSelectedTimes);
    onTimeChange(newSelectedTimes);
  };

  const isTimeSelected = (time: string): boolean => {
    return selectedTimes.includes(time);
  };

  return (
    <div className={styles.container}>
      <div className={styles.timeGrid}>
        {timeSlots.map((time) => {
          const isSelected = isTimeSelected(time);
          const isDisabled = isTimeDisabled(time);

          return (
            <button
              key={time}
              type="button"
              className={`${styles.timeSlot} ${isSelected ? styles.timeSlotSelected : ""} ${
                isDisabled ? styles.timeSlotDisabled : ""
              }`}
              onClick={() => handleTimePress(time)}
              disabled={isDisabled}
            >
              <span
                className={`${styles.timeText} ${isSelected ? styles.timeTextSelected : ""} ${
                  isDisabled ? styles.timeTextDisabled : ""
                }`}
              >
                {time}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
